import { db, functions } from "../../main";
import { getClientRef } from "../../data/repository/ClientRepository";
import { firestore } from "firebase/app";
import { ProjectsModule } from "@/store/modules/ProjectsModule";
import { ArchiveLoadProgress } from "@/utils/ArchiveLoadProgress";
import {
  ARCHIVE_SLICE_PAGE_COUNT,
  DryingProjectOverviewPage,
  mapPageDocumentToOverviewPage,
  mergeDryingProjectOverviewPages,
  resolveArchiveSliceOutcome,
} from "@/utils/ProjectOverviewPages";

/**
 * @module ArchivedProjectsObserver
 *
 * Its own module rather than another export of `ProjectOverviewRepository`, so that the rule it
 * enforces can be unit-tested: the repository imports the project models, which reach the router
 * and half the Vuex modules, and nothing that large can be stood up in a jest run.
 */

/**
 * How many page documents the archive holds, for the progress bar's denominator.
 *
 * Counted in Cloud, because the pinned Firebase 7 web SDK has **no aggregation query**: the
 * browser cannot ask how many slices are coming, and the slice chain only finds out by reaching
 * the end. The Admin SDK can, so `getArchivedProjectOverviewPageCount` answers with one
 * aggregation and no document reads.
 *
 * It replaces remembering the last load's size in `localStorage`, which could not work: `signOut()`
 * clears `localStorage` wholesale, so the remembered size died at every logout and the percentage
 * was missing on exactly the load worth measuring, the first of a session. Counting server-side
 * also leaves nothing about a client in the browser, and there is no stored counter to drift.
 *
 * The callable takes no arguments and addresses the collection from the caller's own token, so
 * this cannot be pointed at another client's archive.
 *
 * @returns the page count, or `null` if it cannot be obtained, which draws an indeterminate bar.
 */
export async function fetchArchivedProjectOverviewPageCount(): Promise<number | null> {
  try {
    const callable = functions.httpsCallable("getArchivedProjectOverviewPageCount");
    const pageCount = (await callable()).data?.pageCount;

    return typeof pageCount === "number" && Number.isFinite(pageCount) && pageCount > 0
      ? pageCount
      : null;
  } catch (error) {
    // A progress bar is never worth failing the archive load over, so a failure here degrades to
    // the indeterminate bar rather than surfacing.
    return null;
  }
}

/**
 * Observes the archive in chained slices rather than as one listener over the whole collection,
 * and reports what has arrived.
 *
 * A single listener emits nothing until the last byte of the collection is in. A production
 * archive of >10k projects is tens of megabytes of overview documents, which on a slow connection
 * is a page showing nothing at all for a minute or more, with no way to tell it apart from a
 * broken one. Slicing gives one real arrival per slice, so the progress bar advances on data
 * rather than on a guess. Every slice is an ordinary `onSnapshot`, so restore, delete and the
 * nightly archive job keep updating the list by themselves.
 *
 * **The slices are not published as they arrive.** The list is sorted by date, so a slice lands
 * anywhere in it, including above what the reader is looking at: publishing each one would leave
 * the page reshuffling and its project count climbing for the whole download, which is worse than
 * waiting. The archive is therefore handed over once, complete and sorted, and only the progress
 * bar moves until then. Afterwards every snapshot is published as usual, because from that point
 * a change is a real change rather than the rest of the first load.
 *
 * While the first load is running, cached snapshots are ignored outright: see the guard in the
 * snapshot handler, which is load-bearing for both the chaining and the hand-over.
 *
 * Each slice starts **at** the previous slice's last document, not after it. A page document
 * created while the page is open shifts every later slice's window by one, and with an exclusive
 * cursor the document pushed over a slice boundary would then belong to no slice at all and
 * disappear from the list. The one-document overlap that buys is removed again when the slices
 * are merged by document id.
 *
 * @param onProgress - called on every slice snapshot, including after the load has completed.
 */
export function observeArchivedProjects(
  onProgress?: (progress: ArchiveLoadProgress) => void
): () => void {
  const collection = db.collection(
    getClientRef() + "/archivedProjectOverviews"
  );

  const subscriptions: Array<() => void> = [];
  const slices = new Map<number, Map<string, DryingProjectOverviewPage>>();

  let isDisposed = false;
  let isComplete = false;
  let hasPublishedInitialLoad = false;

  const emit = () => {
    const pagesByDocumentId = new Map<string, DryingProjectOverviewPage>();

    slices.forEach((slice) => {
      slice.forEach((page, documentId) => {
        pagesByDocumentId.set(documentId, page);
      });
    });

    if (isComplete) {
      hasPublishedInitialLoad = true;
    }

    // Merged on every slice, not only on the one that publishes: the caller counts what has
    // arrived while the list itself waits, and merging is a map build over references.
    const projects = mergeDryingProjectOverviewPages([
      ...pagesByDocumentId.values(),
    ]);

    if (hasPublishedInitialLoad) {
      ProjectsModule.setArchivedProjects(projects);
    }

    onProgress?.({
      loadedPages: pagesByDocumentId.size,
      projects: projects,
      isComplete: isComplete,
    });
  };

  const subscribeToSlice = (sliceIndex: number, startAtDocumentId: string | null) => {
    if (isDisposed) {
      return;
    }

    let query = collection
      .orderBy(firestore.FieldPath.documentId())
      .limit(ARCHIVE_SLICE_PAGE_COUNT);

    if (startAtDocumentId !== null) {
      query = query.startAt(startAtDocumentId);
    }

    let hasChainedNextSlice = false;

    subscriptions.push(
      // `includeMetadataChanges` is what keeps the chain moving, and leaving it out stalls the
      // load outright. A listener whose results are already cached is answered from the cache and
      // then confirmed by the server with the same documents, which is a metadata-only change:
      // without this option the SDK suppresses it, so the cached snapshot below is the only one
      // that ever arrives, the guard drops it, and no further slice is ever subscribed. It bites
      // on the second load of an archive in a session, once every page document is cached.
      query.onSnapshot({ includeMetadataChanges: true }, (snapshot) => {
        // Until the archive has been handed over, only a snapshot from the server counts.
        // Firestore serves a new listener out of its local cache first, and every slice after the
        // first starts at a document the previous slice has already cached, so that opening
        // snapshot holds one document. One document is shorter than a slice, which this reads as
        // the end of the collection: it ends the chain early and, worse, releases the publish
        // latch, which is what put the whole load on screen slice by slice with the count
        // climbing and the list reshuffling under the reader.
        if (!hasPublishedInitialLoad && snapshot.metadata.fromCache) {
          return;
        }

        const pages = new Map<string, DryingProjectOverviewPage>();

        snapshot.forEach((doc) => {
          pages.set(doc.id, mapPageDocumentToOverviewPage(doc));
        });

        slices.set(sliceIndex, pages);

        // The last slice keeps deciding on every snapshot, not only its first: a page document
        // created while the page is open fills it up, and the chain has to grow with the archive
        // or the list would quietly stop at the size it had on arrival.
        if (!hasChainedNextSlice) {
          const outcome = resolveArchiveSliceOutcome(
            snapshot.docs.map((doc) => doc.id),
            startAtDocumentId,
            ARCHIVE_SLICE_PAGE_COUNT
          );

          isComplete = outcome.isComplete;

          if (outcome.nextStartAtDocumentId !== null) {
            hasChainedNextSlice = true;
            subscribeToSlice(sliceIndex + 1, outcome.nextStartAtDocumentId);
          }
        }

        emit();
      })
    );
  };

  subscribeToSlice(0, null);

  return () => {
    isDisposed = true;
    subscriptions.forEach((subscription) => subscription());
  };
}
